import { config } from '@/config'
import { BigNumber } from '@0x/utils'
import { Web3Wrapper } from '@0x/web3-wrapper'
import { orderHashUtils, assetDataUtils } from '@0x/order-utils'
import { SchemaValidator, ValidatorResult, schemas } from '@0x/json-schemas'
import { OrderStatus } from '@0x/contract-wrappers'
import { NULL_ADDRESS, ZERO, TX_DEFAULTS } from '@/config/constants'
import { AssetProxyId } from '@0x/types'
import Vue from 'vue'
// import vuetify from '@/plugins/vuetify'

// --------------------------------------------------------------------------
export const _isMetaDataFormatValid = function (metaData) {

	const metaFields = [
		'makerAsset',
		'takerAsset',
	]

	const optionalMetaFields = [
		'makerFeeAsset',
		'takerFeeAsset',
	]

	const metaSubFields = [
		'name',
		'symbol',
		'decimals',
	]

	for (let key of metaFields) {
		if (metaData[key] === undefined) {
			return false
		}
		for (let key1 of metaSubFields) {
			if (metaData[key][key1] === undefined) {
				return false
			}
		}
	}

	for (let key of optionalMetaFields) {
		if (metaData[key]) {
			for (let key1 of metaSubFields) {
				if (metaData[key][key1] === undefined) {
					return false
				}
			}
		}
	}

	return true
}

// --------------------------------------------------------------------------
export const _isSignedOrderFormatValid = function (signedOrder) {

	const {orderSchema} = schemas
	const validator = new SchemaValidator()
	const isValid = validator.isValid(signedOrder, orderSchema)

	return isValid
}

// --------------------------------------------------------------------------
export const findTokenByAddress = (tokenList, tokenAddress) => {
	for (let token of tokenList) {
		if (token.address === tokenAddress) {
			return token
		}
	}
	return null
}

// --------------------------------------------------------------------------
export const formatAmountNumber = function (value, digits = 6) {
	// console.log('formatAmountNumber', value)
	if (value === null || value === undefined) {
		// console.log('undefined')
		return ''
	}
	if (isNaN(value)) {
		value = parseFloat(value)
	}
	if (value === 0) {
		return 0
	}
	else {
		return parseFloat(value.toFixed(digits))
	}
}

// --------------------------------------------------------------------------
export const formatAllowanceNumber = function (value, digits = 6) {
	if (value === null || value === undefined) {
		// console.log('undefined')
		return ''
	}
	if (isNaN(value)) {
		value = parseFloat(value)
	}
	if (value > config.unlimitedAllowance) {
		return '✔'
	}
	else if (value === 0) {
		return '✖'
	}
	else {
		return formatAmountNumber(value, digits)
	}
}
// --------------------------------------------------------------------------
export const countDecimals = function (value) {
	if (value === null || value === undefined) {
		// console.log('undefined')
		return 0
	}
	if (Math.floor(value) === value) {
		return 0
	}
	const parts = value.toString().split(".")
	// console.log('parts', parts)
	if (parts.length == 1) {
		return 0
	}
	else {
		return parts[1].length || 0;
	}
}

// ---------------------------------------------------------------

export const loadExternalScript = (src) => {
	return new Promise((resolve, reject) => {

		if (document.querySelector('script[src="' + src + '"]')) {
			resolve()
			return
		}

		const el = document.createElement('script')
		el.type = 'text/javascript'
		el.async = true
		el.src = src

		el.addEventListener('load', resolve)
		el.addEventListener('error', reject)
		el.addEventListener('abort', reject)

		document.head.appendChild(el)

	})
}


// --------------------------------------------------------------------------
// Note: time is unix time in milliseconds
export const showDateTime = time => {
	const options = {
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
		hour: 'numeric',
		minute: 'numeric',
		hour12: false
	}
	return (new Date(time)).toLocaleString("sv-SE", options)
}

// --------------------------------------------------------------------------
export const getUnixTimeFromISODate = ISODate => {
	Date.prototype.getUnixTime = function() { return this.getTime()/1000|0 }
	const dateTime = new Date(ISODate)
	return dateTime.getUnixTime()
}

// --------------------------------------------------------------------------
// Get current Unix time, in seconds
// --------------------------------------------------------------------------
export const getCurrentUnixTime = () => {
	return Math.round(new Date().getTime()/1000)
}

// --------------------------------------------------------------------------
// Get current Unix Time, in miliseconds
// --------------------------------------------------------------------------
export const getCurrentUnixTimeInMillisecond = () => {
	return (new Date()).getTime()
}

// --------------------------------------------------------------------------
export const getAssetAddressFromAssetData = assetData => {
	const assetDataDecoded = assetDataUtils.decodeAssetDataOrThrow(assetData)
	return assetDataDecoded.tokenAddress
}

// --------------------------------------------------------------------------
// Given a signed order returned by SRA and metaData with information about
// makerAsset and takerAsset, this function parses the order and returns
// order information in a more user-friendly format (e.g. number in unit
// amount) and also with more data (e.g. priceTakerperMaker)
// --------------------------------------------------------------------------
export const parseOrder = async (signedOrder, metaData) => {

	if (!_isSignedOrderFormatValid(signedOrder)) {
		throw new Error ("signedOrder format is incorrect.")
	}

	if (!_isMetaDataFormatValid(metaData)) {
		console.log(metaData)
		throw new Error ("MetaData format is incorrect.")
	}

	const bigNumberFields = [
		'expirationTimeSeconds',
		'salt',
		'makerAssetAmount',
		'takerAssetAmount',
		'makerFee',
		'takerFee'
	]

	for (let key of bigNumberFields) {
		signedOrder[key] = new BigNumber(signedOrder[key])
	}

	// Get orderHash
	let orderHash = null
	try {
		orderHash = await orderHashUtils.getOrderHashAsync(signedOrder)
	} catch (err) {
		throw new Error ("cannot get orderHash")
	}

	// Decode maker and taker assets
	const makerAssetDecoded = assetDataUtils.decodeAssetDataOrThrow(signedOrder.makerAssetData);
	// console.log(makerAssetDecoded)
	const takerAssetDecoded = assetDataUtils.decodeAssetDataOrThrow(signedOrder.takerAssetData);

	const makerAssetProxyId = makerAssetDecoded.assetProxyId;
	const takerAssetProxyId = takerAssetDecoded.assetProxyId;
	const makerAssetAddress = makerAssetDecoded.tokenAddress;
	const takerAssetAddress = takerAssetDecoded.tokenAddress;

	if (makerAssetProxyId !== AssetProxyId.ERC20) {
		throw new Error ("Not an ERC20 Token: " + signedOrder.makerAssetData)
	}
	if (takerAssetProxyId !== AssetProxyId.ERC20) {
		throw new Error ("Not an ERC20 Token: " + signedOrder.takerAssetData)
	}

	const makerAssetAmount = Web3Wrapper.toUnitAmount(signedOrder.makerAssetAmount, metaData.makerAsset.decimals).toNumber()
	const takerAssetAmount = Web3Wrapper.toUnitAmount(signedOrder.takerAssetAmount, metaData.takerAsset.decimals).toNumber()

  let makerFeeAssetProxyId = null
  let takerFeeAssetProxyId = null
  let makerFeeAssetAddress = null
  let takerFeeAssetAddress = null
  let makerFee = 0
  let takerFee = 0

  if (signedOrder.makerFee > 0) {
  	// console.log('parse makerfee')
  	const makerFeeAssetDecoded = assetDataUtils.decodeAssetDataOrThrow(signedOrder.makerFeeAssetData);
		const makerFeeAssetProxyId = makerFeeAssetDecoded.assetProxyId;
		makerFeeAssetAddress = makerFeeAssetDecoded.tokenAddress;
  	if (makerFeeAssetDecoded.assetProxyId !== AssetProxyId.ERC20) {
  		throw new Error ("Not an ERC20 Token: " + signedOrder.makerFeeAssetData)
  	}
  	if (!metaData.makerFeeAsset) {
  		throw new Error ("Missing makerFeeAsset in metaData")
  	}
  	makerFee = Web3Wrapper.toUnitAmount(signedOrder.makerFee, metaData.makerFeeAsset.decimals).toNumber()
  	// console.log(makerFee)
  }

  if (signedOrder.takerFee > 0) {
  	// console.log('parse takerfee')
  	const takerFeeAssetDecoded = assetDataUtils.decodeAssetDataOrThrow(signedOrder.takerFeeAssetData);
		takerFeeAssetProxyId = takerFeeAssetDecoded.assetProxyId;
		takerFeeAssetAddress = takerFeeAssetDecoded.tokenAddress;
  	if (takerFeeAssetDecoded.assetProxyId !== AssetProxyId.ERC20) {
  		throw new Error ("Not an ERC20 Token: " + signedOrder.takerFeeAssetData)
  	}
  	if (!metaData.takerFeeAsset) {
  		throw new Error ("Missing takerFeeAsset in metaData")
  	}
  	takerFee = Web3Wrapper.toUnitAmount(signedOrder.takerFee, metaData.takerFeeAsset.decimals).toNumber()
  	// console.log(takerFee)
  }

	const priceTakerPerMaker = takerAssetAmount / makerAssetAmount
	const priceMakerPerTaker = makerAssetAmount / takerAssetAmount

	const orderInfo = {
		signedOrder,
		metaData,
		orderHash,
		makerAddress: signedOrder.makerAddress,
		takerAddress: signedOrder.takerAddress,
		feeRecipientAddress: signedOrder.feeRecipientAddress,
		senderAddress: signedOrder.senderAddress,
		exchangeAddress: signedOrder.senderAddress,
		expirationTimeSeconds: signedOrder.expirationTimeSeconds.toNumber(),
		salt: signedOrder.salt.toNumber(),
		makerAssetProxyId,
		takerAssetProxyId,
		makerFeeAssetProxyId,
		takerFeeAssetProxyId,
		makerAssetAddress,
		takerAssetAddress,
		makerAssetAmount,
		takerAssetAmount,
		makerFeeAssetAddress,
		takerFeeAssetAddress,
		makerFee,
		takerFee,
		priceTakerPerMaker,
		priceMakerPerTaker,
	}

	return orderInfo
}


// --------------------------------------------------------------------------
// Given a signed order, this function check the current orderState
// --------------------------------------------------------------------------
export const loadOrderState = async function (signedOrder, metaData, myAddress, account, contractWrappers) {

	if (!_isSignedOrderFormatValid(signedOrder)) {
		throw new Error ("signedOrder format is incorrect.")
	}

	if (!_isMetaDataFormatValid(metaData)) {
		throw new Error ("MetaData format is incorrect.")
	}

	if (!myAddress) {
		throw new Error ("Missing myAddress")
	}

	if (!contractWrappers) {
		throw new Error ("Missing contractWrappers")
	}

	// console.log(OrderStatus)

	const orderState = {
		orderStatus: null,
		marketFillable: true,
		selfFillable: true,
		unfillableReasons: [],
		filledTakerAmount: null,
		availableTakerAmount: null,
		forwarderBuyPossible: false,
		timestamp: null,
	}

	// validate order
	const [
		{ orderStatus, orderHash },
		remainingFillableAmount,
		isValidSignature,
	] = await contractWrappers.devUtils.getOrderRelevantState(signedOrder, signedOrder.signature).callAsync()
	// console.log('orderSatus: ' + OrderStatus[orderStatus])
	// console.log('orderHash: ' + orderHash)
	// console.log('remainingFillableAmount: ' + remainingFillableAmount)
	// console.log('isValidSignature: ' + isValidSignature)

	orderState.orderStatus = orderStatus

	// market fillable or not
	if (orderStatus === OrderStatus.Fillable && remainingFillableAmount.isGreaterThan(0) && isValidSignature) {
		orderState.marketFillable = true
	}
	else {
		orderState.marketFillable = false
		orderState.unfillableReasons.push(OrderStatus[orderStatus])
	}

	// check own order
	if (signedOrder.makerAddress.toLowerCase() === myAddress.toLowerCase()) {
		orderState.selfFillable = false
		orderState.unfillableReasons.push('OWN_ORDER')
	}

	// check if taker specified and does not match
	if (signedOrder.takerAddress != NULL_ADDRESS &&
		signedOrder.takerAddress.toLowerCase() != myAddress.toLowerCase()) {
		orderState.selfFillable = false
		orderState.unfillableReasons.push('NOT_TAKER')
	}

	// Check if there's enough balance and allowance
	const takerAssetDecoded = assetDataUtils.decodeAssetDataOrThrow(signedOrder.takerAssetData)
	const takerAssetAddress = takerAssetDecoded.tokenAddress;
	// console.log('takerAssetAddress: ' + takerAssetAddress)
	if (!account.balanceByAddress[takerAssetAddress] > 0) {
		orderState.selfFillable = false
		orderState.unfillableReasons.push("INSUFFICIENT_TAKER_ASSET")
	}
	if (!account.allowanceByAddress[takerAssetAddress] > 0) {
		orderState.selfFillable = false
		orderState.unfillableReasons.push("INSUFFICIENT_TAKER_ALLOWANCE")
	}

	// Calculate filled and available token amounts
	const makerAssetUnitAmount = Web3Wrapper.toUnitAmount(signedOrder.makerAssetAmount, metaData.makerAsset.decimals).toNumber()
	const takerAssetUnitAmount = Web3Wrapper.toUnitAmount(signedOrder.takerAssetAmount, metaData.takerAsset.decimals).toNumber()
	const remainingFillableTakerUnitAmount = Web3Wrapper.toUnitAmount(new BigNumber(remainingFillableAmount), metaData.takerAsset.decimals).toNumber()

	const filledTakerAssetUnitAmount = takerAssetUnitAmount - remainingFillableTakerUnitAmount
	orderState.filledTakerAmount = filledTakerAssetUnitAmount
	orderState.availableTakerAmount = remainingFillableTakerUnitAmount

	orderState.filledMakerAmount = makerAssetUnitAmount * (orderState.filledTakerAmount / takerAssetUnitAmount)
	orderState.availableMakerAmount = makerAssetUnitAmount - orderState.filledMakerAmount

	// Check whether fillable by instance
	// const WETHAddress = network.contractAddresses.etherToken
	// // console.log('WETHAddress: ' + WETHAddress)
	// if (takerAssetAddress === WETHAddress && orderState.marketFillable && signedOrder.takerAddress === NULL_ADDRESS) {
	// 	// console.log('ETHBalance: ' + account.ETHBalance)
	// 	if (account.ETHBalance > 0) {
	// 		orderState.forwarderBuyPossible = true
	// 	}
	// }

	orderState.timestamp = Math.floor(Date.now() / 1000)

	return orderState

}

// --------------------------------------------------------------------------
export const sortOrderByPriceAsc = (a,b) => {
	if (parseFloat(a.makerAssetAmount) == 0 || parseFloat(b.makerAssetAmount) == 0) {
		return 0
	}
	let price_a = null
	let price_b = null
	if (a.priceTakerPerMaker !== undefined) {
		price_a = a.priceTakerPerMaker
	}
	else {
		price_a = (new BigNumber(a.takerAssetAmount)).toFixed(8) / (new BigNumber(a.makerAssetAmount)).toFixed(8)
	}
	if (b.priceTakerPerMaker !== undefined) {
		price_b = b.priceTakerPerMaker
	}
	else {
		price_b = (new BigNumber(b.takerAssetAmount)).toFixed(8) / (new BigNumber(b.makerAssetAmount)).toFixed(8)
	}
	if (price_a < price_b) return 1
	if (price_a > price_b) return -1
	if (parseInt(a.makerAssetAmount) > parseInt(b.makerAssetAmount)) return 1
	if (parseInt(a.makerAssetAmount) < parseInt(b.makerAssetAmount)) return -1
	return 0
}

// --------------------------------------------------------------------------
export const sortOrderByPriceDes = (a,b) => {
	if (parseFloat(a.makerAssetAmount) == 0 || parseFloat(b.makerAssetAmount) == 0) {
		return 0
	}
	let price_a = null
	let price_b = null
	if (a.priceTakerPerMaker !== undefined) {
		price_a = a.priceTakerPerMaker
	}
	else {
		price_a = (new BigNumber(a.takerAssetAmount)).toFixed(8) / (new BigNumber(a.makerAssetAmount)).toFixed(8)
	}
	if (b.priceTakerPerMaker !== undefined) {
		price_b = b.priceTakerPerMaker
	}
	else {
		price_b = (new BigNumber(b.takerAssetAmount)).toFixed(8) / (new BigNumber(b.makerAssetAmount)).toFixed(8)
	}
	if (price_a > price_b) return 1
	if (price_a < price_b) return -1
	if (parseInt(a.makerAssetAmount) > parseInt(b.makerAssetAmount)) return 1
	if (parseInt(a.makerAssetAmount) < parseInt(b.makerAssetAmount)) return -1
	return 0
}

// --------------------------------------------------------------------------
export const getTradeLogs = async function (contractWrappers, web3Wrapper, networkId, logType, myAddress, fromBlock, toBlock="latest") {

  const bigNumberFields = [
    'makerAssetFilledAmount',
    'takerAssetFilledAmount',
    'protocolFeePaid',
    'makerFeePaid',
    'takerFeePaid'
  ];

  const contractAddresses = contractWrappers.contractAddresses;
  const timeStampHash = {};
  const blockRange = {
    fromBlock: fromBlock,
    toBlock: toBlock,
  };
  const indexFilterValues = {};
  // console.log(blockRange)
  let logs = null;
  try {
    logs = await contractWrappers.exchange.getLogsAsync( logType, blockRange, indexFilterValues);
    // console.log(logs[0])
  } catch (err) {
    console.log(err);
  }

  console.log('new events: ' + logs.length)
  // console.log(logs);
  // console.log(logs[0]);

  const list = [];
  for (let log of logs) {

    let takerIsForwardingContract = false;
    if (log.args.takerAddress === contractAddresses.forwarder) {
    	// console.log('is forwarding contract')
      takerIsForwardingContract = true;
    }

    // Filter out entries that's not for this user
    if (log.args.makerAddress.toLowerCase() !== myAddress.toLowerCase() &&
      log.args.takerAddress.toLowerCase() !== myAddress.toLowerCase() && !takerIsForwardingContract) {
    	// console.log('skip')
			continue
    }

    // convert BigNumber fields to string
    const fields = bigNumberFields;
    for (let key of fields) {
      if (log.args[key] !== undefined) {
        log.args[key] = log.args[key].toString();
      }
    }

    // parse assetData
    let makerAssetDecoded = null
    let takerAssetDecoded = null
    try {
      makerAssetDecoded = assetDataUtils.decodeAssetDataOrThrow(log.args.makerAssetData);
      takerAssetDecoded = assetDataUtils.decodeAssetDataOrThrow(log.args.takerAssetData);

    } catch (err) {
      // console.log(err)
      continue;
    }
    const makerAssetProxyId = makerAssetDecoded.assetProxyId;
    const takerAssetProxyId = takerAssetDecoded.assetProxyId;
    const makerAssetAddress = makerAssetDecoded.tokenAddress;
    const takerAssetAddress = takerAssetDecoded.tokenAddress;

    // Get timestamp
    const blockNumber = log.blockNumber;
    let blockTimeStamp = null;
    let txFromAddress = null;
    let txToAddress = null;

    const tasks = []

    // Get block timestamp
    if (timeStampHash[blockNumber]) {
      // console.log('timetamp found')
      blockTimeStamp = timeStampHash[blockNumber];
    }
    else {
      const getBlockTimeStamp = web3Wrapper.getBlockIfExistsAsync(blockNumber)
        .then( block => {
          blockTimeStamp = block.timestamp;
          timeStampHash[blockNumber] = block.timestamp;
        });
      tasks.push(getBlockTimeStamp);
    }

    // get transaction Info
    const getTransaction = web3Wrapper.getTransactionByHashAsync(log.transactionHash)
      .then( transaction => {
        txFromAddress = transaction.from;
        txToAddress = transaction.to;
      });
    tasks.push(getTransaction);

    console.log('total tasks: ' + tasks.length)
    await Promise.all(tasks);

    if (takerIsForwardingContract && txFromAddress.toLowerCase() !== myAddress) {
    	continue
    }

    const data = {
      ...log.args,
      makerAssetProxyId,
      takerAssetProxyId,
      makerAssetAddress,
      takerAssetAddress,
      blockNumber: log.blockNumber,
      transactionHash: log.transactionHash,
      networkId,
      blockTimeStamp,
      txFromAddress,
      txToAddress,
      takerIsForwardingContract,
    }

    list.push(data);
  }

  // providerEngine.stop();

  return list;
}

// -------------------------------------------------------------------------------
export const sleep = function (ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// --------------------------------------------------------------------------
// Given a signed order, this function calculate the protocol fee
// --------------------------------------------------------------------------
export const calculateProtocolFee = function (orders, gasPrice=TX_DEFAULTS.gasPrice) {
	return new BigNumber(150000).times(gasPrice).times(orders.length)
}

// --------------------------------------------------------------------------
// Given a signed order, this function calculate the protocol fee
// --------------------------------------------------------------------------
export const getCurrentGasPrice = async function (networkId, noCache=false) {

	if (!networkId) {
		console.log('getCurrentGasPrice: Missing networkId')
		return
	}

	const localStorageKey = 'GasPrice-' + networkId

	// First check local storage
	if (!noCache) {
		const localStored = localStorage.getItem(localStorageKey)
		if (localStored) {
			const storedData = JSON.parse(localStored)
			if (storedData.lastLoadTime && storedData.value) {
				const timeNow = getCurrentUnixTimeInMillisecond()
				const lastLoadTime = parseInt(storedData.lastLoadTime)
				if (timeNow - lastLoadTime < config.cacheTime.gasPrice) {
					const price = parseInt(storedData.value)
					// console.log('Got gas price from local storage: ' + price)
					return price
				}
			}
		}
	}

	const priceCheckers = config.network[networkId].gasPriceCheckerURL

	// Then Check ethgasstation
	if (priceCheckers.ethgasstation) {
		const url = priceCheckers.ethgasstation
		try {
			const res = await Vue.http.get(url)
			if (res.body.fast) {
				const origPrice = parseInt(res.body.fast)
				const price = parseInt(origPrice / 10 * 1000000000)
				const timeNow = getCurrentUnixTimeInMillisecond()
				const localSave = {
					lastLoadTime: timeNow,
					value: price
				}
				localStorage.setItem(localStorageKey, JSON.stringify(localSave))
				// console.log('Got gas price from ethgasstation: ' + price)
				return price
			}
			// console.log(res)
		} catch (err) {
			console.log(err)
		}
	}

	// Then Check etherscan
	if (priceCheckers.etherscan) {
		const url = priceCheckers.etherscan
		try {
			const res = await Vue.http.get(url)
			if (res.body.result && res.body.result.ProposeGasPrice) {
				const price = parseInt(res.body.result.ProposeGasPrice) * 1000000000
				const timeNow = getCurrentUnixTimeInMillisecond()
				const localSave = {
					lastLoadTime: timeNow,
					value: price
				}
				localStorage.setItem(localStorageKey, JSON.stringify(localSave))
				// console.log('Got gas price from etherscan: ' + price)
				return price
			}
		} catch (err) {
			console.log(err)
		}
	}

	// If nothing works, return the default value
	// console.log('Cannot get gas price from both. Returning default: ' + TX_DEFAULTS.gasPrice)
	return TX_DEFAULTS.gasPrice

}

// -------------------------------------------------------------------
//
// -------------------------------------------------------------------
export const isProductionCode = function () {
	const hostName = window.location.hostname
  if (hostName && !hostName.includes('localhost') && !hostName.includes('app-st')) {
    return true
  }
  else {
    return false
  }
}
